// Library imports
import React, { useEffect, useState, lazy, Suspense, useRef } from "react";
import store, { RootState } from "store";
import { useSelector } from "react-redux";
import styled from "styled-components";
import axios from "axios";

// Local imports
import UserListItem from "modules/Campaigns/ContactList/UserListItem";
import Checkbox from "modules/Common/Components/Checkbox";
import InfiniteLoader from "modules/Common/Components/InfiniteLoader";
import ContactsListItemLoaderPlaceholder from "./ContactsListItemLoaderPlaceholder";
import {
  deleteProfiledContactFromList,
  getAudienceListDetailProfiledContacts,
  reloadListDetails,
  resetAudienceListDetailsProfiled,
  resetApplySelectedFiltersListDetails,
  resetSelectedFiltersListDetails
} from "./actions";
import DeleteSelected from "../People/DeleteSelected";
import { gillRoyMoveDown, gillRoyMoveUp } from "modules/Content/DragnDrop/actions";
import { getQueryListId } from "utils";
import { AUDIENCE_LISTS_ADD } from "constants/types";
import { deleteSelectedContactAudience, getDesignations, getOrganizations, updateCurrentActiveList } from "../actions";
import EmptyTabInfo from "modules/Content/Shared/EmptyTabInfo";
import { CAMPAIGN_AUDIENCE } from "constants/actionTypes";
import DownAngle from "../../../../assets/img/icons/DownAngle.svg";
import { ReactComponent as DownArrow } from "../../../../assets/img/arrow-down-aud.svg";
import { ReactComponent as UpArrow } from "../../../../assets/img/arrow-top-aud.svg";
import { IContactSorting } from "../interface";
import classNames from "classnames";
import { message } from "antd";

const ConfirmationDialog = lazy(() => import('../Components/ConfirmationDialog'));

interface Props {
  hideIcons?: boolean;
  clearSelection?: boolean;
  isProfiled?: boolean;
  setUpdateTotalCount: (arg0: boolean) => void;
  clearFiltersCheck: boolean;
  exportPeopleFiltersUpdate: () => void;
  tabStateSearch?: boolean;
  addPeopleMode: boolean;
  handleAddPeopleMode: (mode: boolean) => void;
}

const ProfiledList: React.FC<Props> = ({
  clearSelection,
  isProfiled,
  setUpdateTotalCount,
  clearFiltersCheck,
  exportPeopleFiltersUpdate,
  tabStateSearch,
  addPeopleMode,
  handleAddPeopleMode
}) => {
  // Local variables
  let source = axios.CancelToken.source(); // source initialization to be used for cancellation of API request upon unmount
  const DEFAULT_PAGE_LIMIT = 20;

  // Local states
  const [checkedItems, setCheckedItems]: any[] = useState([]);
  const [checkedItemsNewList, setCheckedItemsNewList]: any[] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedProfiled, setSelectedProfiled]: any = useState([]);
  const [deleteAllFlag, setDeleteAllFlag] = useState(false);
  const [deleteAllTotalCount, setDeleteAllTotalCount] = useState(0);
  const [unselectedContactIdsState, setUnselectedContactIdsState]: any[] = useState([]);
  const [firstMount, setFirstMount] = useState(false);
  const [clearFiltersState, setClearFiltersState] = useState(false);
  const [totalCountLocal, setTotalCountLocal] = useState<any>(null);
  const [skipOnMount, setSkipOnMount] = useState(true);
  const [firstMountSearch, setFirstMountSearch] = useState(false);
  const [showList, setShowList] = useState(false);
  const [sortObj, setSortObj] = useState<IContactSorting>({
    sortBy: "contact_name",
    sortType: "ASC",
  });
  const [firstMountSkip, setFirstMountSkip] = useState(true);
  const [singleSelectedProfile, setSingleSelectedProfile]: any = useState([]);
  const [removeSingleContact, setRemoveSingleContact] = useState(false);

  // Redux states
  const listId: string = useSelector((state: RootState) => state.audienceListDetails.listId);
  const listName: string = useSelector((state: RootState) => state.audienceListDetails.listName);
  const profiledPage: number = useSelector((state: RootState) => state.audienceListDetails.profiled.page);
  const profiledLoading: boolean = useSelector((state: RootState) => state.audienceListDetails.profiled.loading);
  const profiledSearchSuccess: boolean = useSelector((state: RootState) => state.audienceListDetails.success);
  const profiledSearchErrorMessage: string = useSelector((state: RootState) => state.audienceListDetails.message);
  const profiledData: any[] = useSelector((state: RootState) => state.audienceListDetails.profiled.data);
  const filters = useSelector((state: RootState) => state.audienceListDetails.filters);
  const deleteLoading: boolean = useSelector((state: RootState) => state.audienceListDetails.delete.loading); // delete loading
  const deleteSuccess: boolean = useSelector((state: RootState) => state.audienceListDetails.delete.success); // check if delete success
  const reloadList = useSelector((state: RootState) => state.audienceListDetails.profiled.reloadList); // reload list after import
  const totalCount = useSelector((state: RootState) => state.audienceListDetails.totalContacts);
  const designationsLoading = useSelector((state: RootState) => state.peopleAudience.designationsLoading);
  const organizationsLoading = useSelector((state: RootState) => state.peopleAudience.organizationsLoading);
  const applyAudienceFilterCheck: boolean = useSelector(
    (state: RootState) => state.uiReducer.applyAudienceFilterCheck
  );
  const audienceFiltersApplied: boolean = useSelector(
    (state: RootState) => state.uiReducer.audienceFiltersApplied
  );
  const searchTermListDetails = useSelector((state: RootState) => state.audienceListDetails.searchTermListDetails);
  const editContactPopup: boolean = useSelector((state: RootState) => state.audience.editContactPopup);
  const newListCreated: boolean = useSelector((state: RootState) => state.audienceLists.newListCreated);
  const activeList = useSelector((state: RootState) => state.peopleAudience.currentActiveList);
  const listContactsRef = useRef(null);
  // Component Logic
  useEffect(() => {
    if (newListCreated) {
      setFirstMountSearch(false);
    }
  }, [newListCreated]);

  useEffect(() => {
    if (firstMountSearch) {
      resetAudienceListDetailsProfiled();
      const params = {
        offset: 0,
        limit: DEFAULT_PAGE_LIMIT,
        filter_by_organization: filters.organizations,
        filter_by_designation: filters.designations,
        personality_type: filters.personality,
        status: filters.status,
        search_term: searchTermListDetails,
        sort_by: sortObj.sortBy,
        sort_type: sortObj.sortType,
      };
      getAudienceListDetailProfiledContacts(params, source.token);
      clearSelectionHandler(true);
    } else {
      setFirstMountSearch(true);
    }
  }, [searchTermListDetails]);

  useEffect(() => {
    if (!skipOnMount) {
      clearSelectionHandler();
      if (newListCreated) {
        store.dispatch({
          type: AUDIENCE_LISTS_ADD.NEWLY_CREATED_LIST_CHECK,
          newListCreated: false,
        });
        setSortObj({ sortBy: "contact_name", sortType: "ASC" }); // set sorting to default if new list created
      }
      const params = {
        offset: 0,
        limit: DEFAULT_PAGE_LIMIT,
        filter_by_organization: [],
        filter_by_designation: [],
        personality_type: [],
        status: [],
        search_term: searchTermListDetails,
        sort_by: newListCreated ? "contact_name" : sortObj.sortBy,
        sort_type: newListCreated ? "ASC" : sortObj.sortType,
      };
      getAudienceListDetailProfiledContacts(params, source.token); // get contacts with empty filters
      setClearFiltersState(true);
      setSelectAll(false);
    } else {
      setSkipOnMount(false);
    }
  }, [clearFiltersCheck]);

  useEffect(() => {
    if (firstMount) {
      const params = {
        offset: getProfiledPagination().offset,
        limit: getProfiledPagination().limit,
        filter_by_organization: filters.organizations,
        filter_by_designation: filters.designations,
        personality_type: filters.personality,
        status: filters.status,
        search_term: searchTermListDetails,
        sort_by: sortObj.sortBy,
        sort_type: sortObj.sortType,
      };
      getAudienceListDetailProfiledContacts(params, source.token);
      clearSelectionHandler(true);
    } else {
      setFirstMount(true);
    }
  }, [applyAudienceFilterCheck]);

  const handleSelectAll = (e: any, paginatedSelectAll: boolean) => {
    if (selectAll && !paginatedSelectAll) {
      setCheckedItems([]);
      setCheckedItemsNewList([]);
      onCheckHandler([]);
    } else {
      const arr = profiledData.map((item) => item.id !== null && item.id !== undefined && item.id);
      setCheckedItems([...arr]);
      const arrNewList = profiledData.map((item) => {
        if (item.id !== null && item.id !== undefined && item.id) {
          const obj = { id: item.id, name: item.name };
          return obj;
        }
      });
      setCheckedItemsNewList([...arrNewList]);
      onCheckHandler([...arr]);
    }

    if (!paginatedSelectAll) setSelectAll(!selectAll);
  };

  // get selected ids
  const onCheckHandler = (values: any) => {
    setSelectedProfiled(values);
    if (values.length > 0) gillRoyMoveUp();
    else gillRoyMoveDown();
  };

  const onClickChecked = (id: any, name: string) => {
    if (checkedItems.includes(id)) {
      if (selectAll) setSelectAll(false);
      let arr = [...checkedItems].filter((item) => item !== id).map((item) => item);
      //! adding this to remove footer
      if (arr.length === 0 && deleteAllFlag) setDeleteAllFlag(false);
      setCheckedItems([...arr]);
      let arrNewList = [...checkedItemsNewList].filter((item) => item?.id !== id).map((item) => item);
      setCheckedItemsNewList([...arrNewList]);
      onCheckHandler(arr);
    } else {
      setCheckedItems([...checkedItems, id]);
      setCheckedItemsNewList([...checkedItemsNewList, { id, name }]);
      onCheckHandler([...checkedItems, id]);
      if (!selectAll && [...checkedItems, id].length === profiledData.length) setSelectAll(true);
    }
  };

  useEffect(() => {
    if (clearFiltersState) {
      setSelectAll(false);
      setCheckedItems([]);
      setCheckedItemsNewList([]);
      setSelectedProfiled([]);
      setUnselectedContactIdsState([]);
      setClearFiltersState(false);
    }
  }, [clearFiltersState]);

  //  on Delete Handler
  const deleteHandler = async () => {
    const params = {
      offset: 0,
      limit: getProfiledPagination().limit,
      filter_by_organization: filters.organizations,
      filter_by_designation: filters.designations,
      personality_type: filters.personality,
      status: filters.status,
      search_term: searchTermListDetails,
      sort_by: sortObj.sortBy,
      sort_type: sortObj.sortType,
    };

    const countToDelete: any = deleteAllTotalCount - unselectedContactIdsState?.length;
    deleteProfiledContactFromList(
      removeSingleContact ? [Number(singleSelectedProfile[0]?.id)] : selectedProfiled,
      deleteAllFlag,
      undefined,
      undefined,
      deleteAllFlag && unselectedContactIdsState,
      deleteAllFlag && countToDelete,
      filters.organizations,
      filters.designations,
      filters.personality,
      filters.status,
      searchTermListDetails,
      params,
      source.token

    ).then((res) => {
      message.success({
        content: `${removeSingleContact ? singleSelectedProfile[0]?.name : "Contacts"} has been removed from the list.`,
      });
    })
    setCheckedItems([]);
    setCheckedItemsNewList([]);
    setSelectAll(false);
    if (deleteAllFlag) {
      setDeleteAllFlag(false);
    }
    gillRoyMoveDown();
  };

  const permanentDeleteHandler = async () => {
    // filter unselectedContactIdsState from profiledData to prevent masterlist contacts from deletion 
    deleteSelectedContactAudience(
      selectedProfiled,
      "all",
      deleteAllFlag,
      deleteAllFlag && unselectedContactIdsState,
      searchTermListDetails,
      filters.organizations,
      filters.designations,
      filters.personality,
      filters.status,
      true,
      listId
    );
    setCheckedItemsNewList([]);
    setCheckedItems([]);
    setSelectAll(false);
    if (deleteAllFlag) {
      setDeleteAllFlag(false);
    }
    gillRoyMoveDown();
  };

  const getProfiledPagination = (reset?: boolean) => {
    let offset = profiledPage > 0 && !reset ? profiledData.length : 0;
    let limit = DEFAULT_PAGE_LIMIT; //update to 4
    if (reset) offset = 0;
    const search_term = searchTermListDetails;
    const sort_by = sortObj.sortBy;
    const sort_type = sortObj.sortType;
    return { offset, limit, search_term, sort_by, sort_type };
  };

  const loadMore = () => {
    if (!profiledLoading) {
      let params: { [key: string]: any } = {
        ...getProfiledPagination()
        // offset: getProfiledPagination().offset,
        // limit: getProfiledPagination().limit,
        // search_term: searchTermListDetails,
        // sort_by: sortObj.sortBy,
        // sort_type: sortObj.sortType,
      };
      if (audienceFiltersApplied) {
        params = {
          ...params,
          filter_by_organization: filters.organizations,
          filter_by_designation: filters.designations,
          personality_type: filters.personality,
          status: filters.status,
        }
      }
      getAudienceListDetailProfiledContacts(params);
    }
  };

  const clearSelectionHandler = (resetFilters?: boolean) => {
    setCheckedItems([]);
    setCheckedItemsNewList([]);
    setSelectAll(false);
    setSelectedProfiled([]);
    setDeleteAllFlag(false);
    exportPeopleFiltersUpdate();
    if (!resetFilters) {
      resetApplySelectedFiltersListDetails();
      resetSelectedFiltersListDetails();
    }
  };

  useEffect(() => {
    if (deleteSuccess) {
      setSelectedProfiled([]);
    }
  }, [deleteSuccess]);

  //  for clearing selection when clicked from delete bottom bar
  useEffect(() => {
    if (clearSelection && checkedItems.length > 0) {
      setCheckedItems([]);
      setCheckedItemsNewList([]);
      setSelectAll(false);
    }
  }, [clearSelection]);

  useEffect(() => {
    if (!profiledLoading) {
      let params: { [key: string]: any } = {
        offset: 0,
        limit: DEFAULT_PAGE_LIMIT,
        search_term: searchTermListDetails,
        sort_by: sortObj.sortBy,
        sort_type: sortObj.sortType,
      };
      if (audienceFiltersApplied) {
        params = {
          ...params,
          filter_by_organization: filters.organizations,
          filter_by_designation: filters.designations,
          personality_type: filters.personality,
          status: filters.status,
        }
      }
      if (listId !== getQueryListId().toString()) {
        resetAudienceListDetailsProfiled();
      }
      getAudienceListDetailProfiledContacts(params);
    }

    return () => {

    };
  }, []);

  useEffect(() => {
    if (reloadList && !profiledLoading) {
      const params = {
        offset: 0,
        limit: DEFAULT_PAGE_LIMIT,
        filter_by_organization: filters.organizations,
        filter_by_designation: filters.designations,
        personality_type: filters.personality,
        status: filters.status,
        search_term: searchTermListDetails,
        sort_by: sortObj.sortBy,
        sort_type: sortObj.sortType,
      };
      getAudienceListDetailProfiledContacts(params);
      reloadListDetails(false, "profiled");
    }
  }, [reloadList]);

  useEffect(() => {
    if (selectAll) {
      handleSelectAll(null, true);
    }
    if (deleteAllFlag && !selectAll) handleSelectPaginatedContacts(null, true);
  }, [profiledData]);

  useEffect(() => {
    setDeleteAllTotalCount(totalCount);
    if (totalCountLocal !== totalCount && totalCountLocal > 0 && !organizationsLoading && !designationsLoading) {
      getDesignations(getQueryListId().toString());
      getOrganizations(getQueryListId().toString());
    }
    setTotalCountLocal(totalCount);
  }, [totalCount]);

  useEffect(() => {
    if (selectAll) setUnselectedContactIdsState([]);
  }, [selectAll]);

  const contactCheckHandler = (id: string, isChecked: boolean) => {
    if (isChecked) {
      if (!unselectedContactIdsState.includes(id)) {
        const array = [...unselectedContactIdsState, id];
        setUnselectedContactIdsState(array);
      }
    }
  };

  const handleReselectItem = (id: string) => {
    const array = unselectedContactIdsState
      .filter((itemId: string) => itemId.toString() !== id.toString())
      .map((itemId: string) => itemId);
    setUnselectedContactIdsState(array);
  };

  //  Select the new contacts is deleteAllFlag is true
  const handleSelectPaginatedContacts = (e: any, paginatedSelectAll: boolean) => {
    if (deleteAllFlag && !selectAll && !paginatedSelectAll) {
      setCheckedItems([]);
      setCheckedItemsNewList([]);
      if (onCheckHandler) onCheckHandler([]);
    } else {
      const arr = profiledData
        .filter((item: any) => !unselectedContactIdsState.includes(item.id.toString()))
        .map((item: any) => item.id);
      setCheckedItems([...arr]);
      const arrNewList = profiledData
        .filter((item: any) => !unselectedContactIdsState.includes(item.id.toString()))
        .map((item: any) => {
          return { id: item.id, name: item.name };
        });
      setCheckedItemsNewList([...arrNewList]);
      if (onCheckHandler) onCheckHandler([...arr]);
    }

    if (!paginatedSelectAll) setSelectAll(!selectAll);

    /**
     *! commenting the following code as it's causing the issue of not selecting
     *! the contacts when deleteAllFlag is true on pagination
     */

    /*
      if (clearFiltersState) {
        setSelectAll(false);
        setCheckedItems([]);
        setCheckedItemsNewList([]);
        setSelectedProfiled([]);
        setUnselectedContactIdsState([]);
        setClearFiltersState(false);
      } 
    */
  };

  const scrollHandler = (e: any) => {
    if (editContactPopup) {
      store.dispatch({
        type: CAMPAIGN_AUDIENCE.SET_EDIT_CONTACT_POPOVER_STATE,
        payload: { editContactPopup: false },
      });
    }
  };

  const sortHandler = (sortingColumn: string) => {
    clearSelectionHandler(true); // clear any selected contacts
    resetAudienceListDetailsProfiled(); // reset contacts
    if (sortingColumn === "contact_name") {
      if (sortObj.sortType === "ASC") {
        setSortObj({ sortBy: sortingColumn, sortType: "DESC" }); // sort by ascending
      } else {
        setSortObj({ sortBy: sortingColumn, sortType: "ASC" }); // sort by descending
      }
    } else {
      if (sortObj.sortBy === sortingColumn) {
        if (sortObj.sortType === "") {
          setSortObj({ sortBy: sortingColumn, sortType: "ASC" }); // sort by ascending
        } else if (sortObj.sortType === "ASC") {
          setSortObj({ sortBy: sortingColumn, sortType: "DESC" }); // sort by descending
        } else {
          setSortObj({ sortBy: "contact_name", sortType: "ASC" }); // reset  sorting to default
        }
      } else {
        setSortObj({ sortBy: sortingColumn, sortType: "ASC" });
      }
    }
  };

  useEffect(() => {
    if (!firstMountSkip) {
      const params = {
        offset: 0,
        limit: DEFAULT_PAGE_LIMIT,
        filter_by_organization: filters.organizations,
        filter_by_designation: filters.designations,
        personality_type: filters.personality,
        status: filters.status,
        search_term: searchTermListDetails,
        sort_by: sortObj.sortBy,
        sort_type: sortObj.sortType,
      };
      getAudienceListDetailProfiledContacts(params, source.token);
    } else {
      setFirstMountSkip(false);
    }
  }, [sortObj]);

  useEffect(() => {
    if (checkedItems.length > 0) {
      updateCurrentActiveList("list")
    }
    // if currentActive list is List and user change search, it shouldn't reset the currentActive to default.
    // Only reset if current list is "list" and search changes.
    else if (activeList !== "global_list") {
      updateCurrentActiveList("")
    }
  }, [checkedItems])


  const handleRemoveSingleContact = (args: { flag: boolean, name?: string, contact_id?: number | string }) => {
    const { flag, name, contact_id } = args;
    setRemoveSingleContact(flag)

    if (flag && name && contact_id) {
      setSingleSelectedProfile([{ name, id: contact_id }])
    } else if (!flag) {
      clearSelectionHandler(true);
      setSingleSelectedProfile([]);
    }
  }

  const handleRemoveFromExistingList = () => {
    deleteHandler();
  }

  const checkCurrentActiveList = () => {
    if (activeList === "list" || activeList === "") return false
    return true
  }

  return (
    <Wrapper
      profiledLoading={profiledLoading}
      className={classNames("person-table-wrapper", { tabGate: tabStateSearch, extend: showList })}
    >
      <InfiniteLoader
        dataLength={profiledData.length}
        next={loadMore}
        hasMore={totalCount > profiledData.length ? true : false}
        loader={<></>}
        scrollableTarget="scrollableDiv"
      >
        <div
          onScroll={scrollHandler}
          id="scrollableDiv"
          className={`scrollable-table mt-2 ${checkedItems.length > 0 ? "set-table-height" : ""}`}
          ref={listContactsRef}
        >
          <>
            <table>
              <thead hidden={profiledData.length === 0 && !profiledLoading}>
                <tr>
                  <td className={`checkbox`}>
                    <Checkbox
                      disabled={checkCurrentActiveList()}
                      onClick={() => setDeleteAllFlag && setDeleteAllFlag(!selectAll)}
                      checked={selectAll}
                      onChange={handleSelectAll}
                      data-id="qa-profiled-checkbox"
                    />
                  </td>
                  {/* Sorting by Name */}
                  <td className="h-name">
                    <span className="sortable-td" onClick={() => sortHandler("contact_name")}>
                      <span className="left-align">Name</span>
                      <span
                        className={`upArrow arrow-opacity ${sortObj.sortBy === "contact_name" && sortObj.sortType === "DESC" ? "transparent" : ""
                          }`}
                      >
                        <img src={DownAngle} alt="Sorting" />
                      </span>
                      <span
                        className={`downArrow arrow-opacity ${sortObj.sortBy === "contact_name" && sortObj.sortType === "ASC" ? "transparent" : ""
                          }`}
                      >
                        <img src={DownAngle} alt="Sorting" />
                      </span>
                    </span>
                  </td>
                  {/* Sorting by Company */}
                  <td className="company-col">
                    <span className="sortable-td" onClick={() => sortHandler("company_name")}>
                      <span className="left-align">Company</span>
                      <span
                        className={`upArrow arrow-opacity ${sortObj.sortBy === "company_name" && sortObj.sortType === "DESC" ? "transparent" : ""
                          }`}
                      >
                        <img src={DownAngle} alt="Sorting" />
                      </span>
                      <span
                        className={`downArrow arrow-opacity ${sortObj.sortBy === "company_name" && sortObj.sortType === "ASC" ? "transparent" : ""
                          }`}
                      >
                        <img src={DownAngle} alt="Sorting" />
                      </span>
                    </span>
                  </td>
                  {/* Sorting by Personality */}
                  <td className="personality-col">
                    <span className="sortable-td" onClick={() => sortHandler("personality_name")}>
                      <span className="left-align">Personality</span>
                      <span
                        className={`upArrow arrow-opacity ${sortObj.sortBy === "personality_name" && sortObj.sortType === "DESC" ? "transparent" : ""
                          }`}
                      >
                        <img src={DownAngle} alt="Sorting" />
                      </span>
                      <span
                        className={`downArrow arrow-opacity ${sortObj.sortBy === "personality_name" && sortObj.sortType === "ASC" ? "transparent" : ""
                          }`}
                      >
                        <img src={DownAngle} alt="Sorting" />
                      </span>
                    </span>
                  </td>
                  {/* Sorting by Status */}
                  <td className="status-col">
                    <span className="sortable-td" onClick={() => sortHandler("status")}>
                      <span className="left-align">Status</span>
                      <span
                        className={`upArrow arrow-opacity ${sortObj.sortBy === "status" && sortObj.sortType === "DESC" ? "transparent" : ""
                          }`}
                      >
                        <img src={DownAngle} alt="Sorting" />
                      </span>
                      <span
                        className={`downArrow arrow-opacity ${sortObj.sortBy === "status" && sortObj.sortType === "ASC" ? "transparent" : ""
                          }`}
                      >
                        <img src={DownAngle} alt="Sorting" />
                      </span>
                    </span>
                  </td>
                  {tabStateSearch ? null : <td>&nbsp;</td>}
                  {/* Sorting by Engagement Score */}
                  <td className="last-item">
                    <span className="sortable-td" onClick={() => sortHandler("engagement_score")}>
                      <span className="engagement-float d-none">Engagement Score</span>
                      <span
                        className={`upArrow engagement-arrow arrow-opacity d-none ${sortObj.sortBy === "engagement_score" && sortObj.sortType === "DESC" ? "transparent" : ""
                          }`}
                      >
                        <img src={DownAngle} alt="Sorting" />
                      </span>
                      <span
                        className={`downArrow engagement-arrow arrow-opacity d-none ${sortObj.sortBy === "engagement_score" && sortObj.sortType === "ASC" ? "transparent" : ""
                          }`}
                      >
                        <img src={DownAngle} alt="Sorting" />
                      </span>
                    </span>
                  </td>
                  {tabStateSearch ? null : <td></td>}
                </tr>
              </thead>
              <tbody>
                {profiledLoading && profiledData.length === 0
                  ? null
                  : profiledData.length > 0
                    ? profiledData?.map((contact: any, index: number) => {
                      return (
                        <UserListItem
                          isProfiled={isProfiled}
                          key={contact.id}
                          contact={contact}
                          isChecked={checkedItems.includes(contact.id)}
                          onClickChecked={() => onClickChecked(contact.id, contact.name)}
                          hideIcons={true}
                          hideEllipsesMenu={true}
                          deleteAllFlag={deleteAllFlag}
                          contactCheckHandler={contactCheckHandler}
                          handleReselectItem={handleReselectItem}
                          status={contact.status}
                          index={index}
                          tabStateSearch={tabStateSearch}
                          handleRemoveSingleContact={handleRemoveSingleContact}
                        />
                      );
                    })
                    : null}
              </tbody>
            </table>
          </>
          {profiledLoading ? <ContactsListItemLoaderPlaceholder loading={profiledLoading} count={4} /> : null}
          {profiledData.length === 0 &&
            !profiledLoading &&
            searchTermListDetails?.length === 0 &&
            !audienceFiltersApplied && (
              <div>
                <EmptyTabInfo description="Your contacts will show up here once you will import a list or create new contact." />
              </div>
            )}
          {(searchTermListDetails?.length > 0 || audienceFiltersApplied) &&
            !profiledLoading &&
            profiledData.length === 0 && (
              <>
                <EmptyTabInfo
                  description={
                    profiledSearchSuccess ? `No contact(s) found against your search in the list (${listName})` : profiledSearchErrorMessage
                  }
                />
              </>
            )}
        </div>
      </InfiniteLoader>
      {tabStateSearch ? profiledData.length > 2 ? (
        <ViewMore className={classNames("view-more viewMoreAppend")} onClick={() => {
          setShowList(!showList)
          listContactsRef?.current?.scrollTo(0, 0)
        }}>
          {showList ? (
            <span className="arrow-text">
              View Less <UpArrow />
            </span>
          ) : (
            <span className="arrow-text">
              View All <DownArrow />
            </span>
          )}
        </ViewMore>
      ) : null : null}

      <DeleteSelected
        inProfiled={true}
        unselectedContactIdsState={unselectedContactIdsState}
        checkedItemsNewList={checkedItemsNewList}
        contactsInList={true}
        deleteAllFlag={deleteAllFlag}
        deleteAllTotalCount={totalCount - unselectedContactIdsState.length}
        checkedItems={checkedItems}
        selectAllList={selectAll}
        selectedCount={checkedItems.length}
        loading={deleteLoading}
        deleteSuccess={deleteSuccess}
        onClearSelection={clearSelectionHandler}
        onDeleteHandler={deleteHandler}
        selectedOrganization={filters.organizations}
        selectedDesignation={filters.designations}
        selectedPersonality={filters.personality}
        selectedStatus={filters.status}
        addPeopleMode={addPeopleMode}
        handleRemoveFromExistingList={handleRemoveFromExistingList}
        globalList={false}
        permanentDeleteHandler={permanentDeleteHandler}
        idForRecommendedContacts={"profiled-list"}
      />
      <Suspense fallback={<></>}>
        <ConfirmationDialog
          addPeopleMode={addPeopleMode}
          globalList={false}
          removeSingleContact={removeSingleContact}
          selectedProfiles={removeSingleContact ? singleSelectedProfile : selectedProfiled}
          handleConfirmation={handleRemoveFromExistingList}
          handleSetSingleContact={handleRemoveSingleContact}
        />
      </Suspense>
    </Wrapper>
  );
};

export default ProfiledList;

const Wrapper = styled.div<{ profiledLoading: boolean }>`
  .sortable-td {
    cursor: ${(props) => (props.profiledLoading ? "auto" : "pointer")};
    pointer-events: ${(props) => (props.profiledLoading ? "none" : "auto")};
  }
  .arrow-opacity {
    opacity: ${(props) => (props.profiledLoading ? "0.5" : "1")};
  }
  .transparent {
    opacity: 0.5;
  }
  .engagement-float {
    float: revert !important;
  }
  &.tabGate {
    &.person-table-wrapper {
      .scrollable-table {
        overflow-y: hidden;
        max-height: 240px;
        height: auto;
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          max-height: 180px;
        }
      }
    }
    &.extend {
      &.person-table-wrapper {
        .scrollable-table {
          max-height: 315px;
          overflow-y: auto;
          height: auto;
        }
      }
    }
    table {
      thead {
        td.h-name {
          width: 472px;
          @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
            width: 330px;
          }
        }
      }
    }
  }
  .table-style {
    cursor: pointer;
    height: 95px;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      height: 69px;
    }
  }
  td {
    span:first-child {
      &.left-align {
        float: left;
        margin-top: -7px;
      }
    }
    &.last-item {
      span {
        float: right;
        margin-top: -7px;
      }
    }
  }
  .disable {
    pointer-events: none;
    opacity: 0.5;
  }
  .upArrow {
    width: 10px;
    height: 8px;
    margin-top: -16px;
    margin-left: 5px;
    float: left;
    cursor: pointer;
    @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
      margin-top: -14px;
    }
    img {
      transform: rotate(180deg);
    }
    &.engagement-arrow {
      float: right;
      margin-top: -9px;
      @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
        margin-top: -8px;
      }
    }
  }
  .downArrow {
    width: 10px;
    height: 8px;
    float: left;
    margin-left: 5px;
    margin-top: 8px;
    cursor: pointer;
    @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
      margin-top: 6px;
    }
    img {
      float: left;
    }
    &.engagement-arrow {
      float: right;
      margin-top: 14px;
      @media all and (max-width: 1439px), (-webkit-device-pixel-ratio: 1.25) {
        margin-top: 12px;
      }
      img {
        float: right;
      }
    }
  }
  .scrollable-table {
    margin-bottom: 0px;
    position: relative;
    box-sizing: border-box;
    overflow-y: auto;
    height: calc(100vh - 300px);
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      height: calc(100vh - 258px);
    }
    &.set-table-height {
      height: calc(100vh - 405px);
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        height: calc(100vh - 328px);
      }
    }
    scrollbar-width: thin;
    scrollbar-color: #001760 #efefef;
    &::-webkit-scrollbar {
      width: 5px;
      height: 5px;
      border-radius: 5px;
    }
    /* Track */
    &::-webkit-scrollbar-track {
      background: #fff;
      margin-top: 50px;
      margin-bottom: 0px;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        margin-top: 40px;
      }
    }
    /* Handle */
    &::-webkit-scrollbar-thumb {
      background: #001760;
    }
    > div {
      overflow: hidden;
    }
  }
  .checked {
    border: 1px solid blue;
    border-radius: 10px;
  }
  table {
    width: 100%;
    border: none;
    border-collapse: collapse;
    margin-bottom: 0px;
    thead {
      tr {
        &:before {
          max-height: 50px;
          @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
            max-height: 38px;
          }
        }
      }
      td {
        color: #5e7079;
        font-size: 18px;
        font-family: "robotomedium";
        font-weight: 500;
        white-space: nowrap;
        position: sticky;
        top: 0;
        z-index: 1;
        background-color: #f9f8f6;
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          font-size: 14px;
        }
        &.h-name {
          padding-left: 75px;
          width: 472px;
          @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
            padding-left: 65px;
            //Safari (7.1+)
            _::-webkit-full-page-media,
            _:future,
            :root .safari_only {
              width: 300px;
            }
            //Safari (from 6.1 to 10)
            @media screen and (min-color-index: 0) and(-webkit-min-device-pixel-ratio:0) {
              @media {
                width: 300px;
              }
            }
            //Safari (10.1+)
            @media not all and (min-resolution: 0.001dpcm) {
              @media {
                width: 300px;
              }
            }
          }
          @media all and (max-width: 1440px) {
            width: 450px;
          }
        }
        &.company-col {
          width: 270px;
        }
        &.personality-col {
          min-width: 127px;
        }
        &.status-col {
          min-width: 118px;
        }
        &.last-item {
          /* text-align: right;
          padding-right: 45px; */
          min-width: 190px;
          @media all and (max-width: 1590px) {
            min-width: 226px;
          }
        }
        &.industry {
          display: flex;
          justify-content: center;
        }
      }
    }
    tr {
      border-radius: 8px;
      transition-duration: 0.2s;
      position: relative;
      &:before {
        content: "";
        border-bottom: 1px solid #dddddd;
        position: absolute;
        width: 100%;
        height: calc(100% + 3px);
        transition-duration: 0.2s;
        box-sizing: border-box;
        margin-top: -2px;
        height: 1px;
        @media all and (min-width: 1440px) {
          margin-top: -3px;
        }
      }
      &:first-child {
        &.active-new {
          &:before {
            max-height: 95px !important;
            margin-top: -1px;
          }
        }
      }
      &.active-new {
        background: #cdf1d9;
        border-radius: 8px;
        border-bottom: none;
        &:before {
          max-height: 95px !important;
          border: 3px solid #27ce0a;
          border-radius: 8px;
          height: calc(100% + 2px);
          _::-webkit-full-page-media,
          _:future,
          :root .safari_only {
            height: 69px;
          }
          /* Safari (from 6.1 to 10)  */
          @media screen and (min-color-index: 0) and(-webkit-min-device-pixel-ratio:0) {
            @media {
              /* Your Class */
              height: 69px;
            }
          }
          /* Safari (10.1+)  */
          @media not all and (min-resolution: 0.001dpcm) {
            @media {
              /* Your Class */
              height: 69px;
            }
          }
        }
        td {
          overflow: hidden;
          &:first-child {
            border-top-left-radius: 8px;
            border-bottom-left-radius: 8px;
          }
          &:last-child {
            border-top-right-radius: 8px;
            border-bottom-right-radius: 8px;
          }
        }
      }
      &.disabled {
        pointer-events: none;
        opacity: 0.5;
      }
      &.active {
        background: #fff;
        border-radius: 8px;
        td {
          overflow: hidden;
          &:first-child {
            border-top-left-radius: 8px;
            border-bottom-left-radius: 8px;
          }
          &:last-child {
            border-top-right-radius: 8px;
            border-bottom-right-radius: 8px;
          }
        }
      }
      &.drag-active {
        position: relative;
        border-radius: 8px;
        td {
          overflow: hidden;
          &:first-child {
            border-top-left-radius: 8px;
            border-bottom-left-radius: 8px;
          }
          &:last-child {
            border-top-right-radius: 8px;
            border-bottom-right-radius: 8px;
          }
        }
        &:after {
          content: "";
          height: 100%;
          width: calc(100% + 10px);
          position: absolute;
          top: 0;
          left: 0;
          z-index: 99999;
          background-color: #f2efeb;
          border-radius: 8px;
          margin: 0 -5px;
          display: block;
        }
      }
      td {
        padding: 9px 10px;
        &.checkbox {
          width: 10px;
          padding-right: 0 !important;
        }
        &.last-item {
          text-align: right;
          padding-right: 45px;
        }
        .profile-img-wrapper {
          padding: 2px;
          width: 54px;
          height: 54px;
          border-radius: 100%;
          float: left;
          overflow: hidden;
          margin-top: 0px;

          @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
            width: 41px;
            height: 41px;
          }
          .profile-img {
            border-radius: 100%;
            width: 100%;
            height: 100%;
          }
        }
        .profile {
          float: left;
          padding-left: 14px;
          text-align: left;
          width: 250px;
          max-width: calc(100% - 55px);
          //Safari (7.1+)
          _::-webkit-full-page-media,
          _:future,
          :root .safari_only {
            width: 250px;
          }
          //Safari (from 6.1 to 10)
          @media screen and (min-color-index: 0) and(-webkit-min-device-pixel-ratio:0) {
            @media {
              width: 250px;
            }
          }
          //Safari (10.1+)
          @media not all and (min-resolution: 0.001dpcm) {
            @media {
              width: 250px;
            }
          }
          .name {
            color: #000000;
            font-size: 20px;
            font-family: "Roboto Slab", serif;
            margin-bottom: -5px;
            @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
              font-size: 14px;
            }
            &.designation {
              color: rgba(0, 0, 0, 0.8);
            }
          }
          .hidden-contact-name {
            height: 1px;
            overflow: hidden;
            display: block;
            opacity: 0;
            font-size: 20px;
            font-family: "Roboto Slab", serif;
          }
          .email {
            color: #27ce0a;
            font-size: 14px;
            font-family: "robotomedium";
            font-weight: 500;
            margin-bottom: 0;
            @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
              font-size: 10px;
            }
            &.designation {
              color: rgba(0, 0, 0, 0.8);
            }
          }
        }
        .organization {
          display: block;
          width: 100%;
          color: #424b50;
          font-size: 18px;
          font-family: "robotomedium";
          font-weight: 500;
          white-space: nowrap;
          text-overflow: ellipsis;
          max-width: 100%;
          &.company {
            width: 200px;
            max-width: calc(100% - 50px);
            overflow: hidden;
          }

          @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
            font-size: 13px;
          }
        }
        .status {
          color: #424b50;
          font-size: 18px;
          font-family: "robotomedium";
          font-weight: 500;
          &.status-style-sub {
            color: #1cb84d;
            font-weight: bold;
          }
          &.status-style-unsub {
            color: #d50508;
            font-weight: bold;
          }
          &.status-style-bounce {
            color: #ff9531;
            font-weight: bold;
          }
          @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
            font-size: 13px;
          }
        }
        .personality {
          color: #424b50;
          font-size: 18px;
          font-family: "robotomedium";
          font-weight: 500;
          @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
            font-size: 13px;
          }
        }
        .industry {
          display: flex;
          justify-content: center;
          width: 100%;
          font-size: 14px;
          font-family: "robotomedium";
          font-weight: 500;
          @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
            font-size: 12px;
          }
        }
        .actions {
          ul {
            list-style: none;
            margin: 0 -10px;
            padding: 0;
            li {
              float: left;
              display: inline-block;
              padding: 0 3px;
              @media all and (max-width: 1200px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
                padding: 0;
              }
              a {
                padding: 0 5px;
                text-align: center;
                display: block;
                @media all and (max-width: 1300px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
                  padding: 0 3px;
                }
                svg {
                  width: 18px;
                  max-height: 12px;
                  path {
                    fill: #000000;
                    opacity: 1;
                  }
                  g {
                    opacity: 1;
                  }
                }
                span {
                  color: #5e7079;
                  font-size: 10px;
                  font-family: "helveticaneuebold";
                  font-weight: 700;
                  display: block;
                  text-align: center;
                }
              }
            }
          }
        }
        .user-aud-area {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          .engagement {
            margin: 0px 15px 0px 0px;
          }
          .aud-action {
            cursor: pointer;
            svg {
              width: 20px;
              display: inline-block;
            }
          }
        }

        .engagement {
          border-radius: 12px;
          background-color: #1971eb;
          color: #ffffff;
          font-size: 16px;
          font-family: "robotomedium";
          font-weight: 500;
          padding: 0px 10px;
          margin: 0 auto;
          min-width: 80px;
          width: fit-content;
          float: right;
          display: none;
          img {
            float: left;
            margin-top: 7px;
          }
          @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
            font-size: 12px;
            img {
              max-width: 13px;
              margin-top: 5px;
            }
          }
        }
        .float-right {
          float: right;
          margin-left: 15px;
        }
      }
    }
  }
`;

const ViewMore = styled.div`
  background-color: #fff;
  display: none;
  padding-bottom: 16px;
  display: flex;
  justify-content: flex-end;
  padding-right: 45px;
  padding-top: 10px;
  cursor: pointer;
  &.viewMoreAppend {
    display: block;
    border-radius: 0px 0px 8px 8px;
    text-align: right;
  }
  .arrow-text {
    font-family: "Roboto";
    font-style: normal;
    font-weight: 700;
    font-size: 15.4688px;
    line-height: 14px;
    letter-spacing: -0.386719px;
    color: #1ec252;
    svg {
      margin-left: 15px;
    }
  }
`;
